import { computed, ref, nextTick } from "vue";
import fetchJsonp from "fetch-jsonp";
import { useDebounceFn } from "@vueuse/core";
import { tr_searchEvent } from "@/SearchApp/services/dataLayer";
import { contextPath, language } from "@ocm/services/ocm-object";
import useDepartment from "./use-department";
import useMostSuggestedRequest from "./use-most-suggested-request";
import useFindings, { findingsSize } from "./use-findings";
import { getEpoqRequestParams } from "@/SearchApp/services/epoq-request-settings";
import {
	EPOQ_SEARCH_URL,
	EPOQ_SEARCH_FALLBACK_URL,
	EPOQ_SEARCH_FALLBACK_TIMEOUT,
	epoqRequestTimeout,
} from "@ocm/services/services.epoq";
import type { EpoqResult } from "@/SearchApp/types/epoq.type";
import { messengerPublicationTypes } from "@ocm/services/services.constants";
import OcmMessenger from "ocm_mercurius_messenger";

export const focusedTypeAheadId = ref<string | undefined>();
export const focusedTypeAheadIndex = ref<number>(-1);

const searchPagePath = `${contextPath}/${language === "de" ? "suche" : "search"}/`;
const searchCategoryHashLabel = language === "de" ? "Kategorie" : "Category";

const requestFinished = ref(true);
const isLoading = ref(false);
const _setLoading = () => {
	if (!requestFinished.value) isLoading.value = true;
};
const _debounceLoading = useDebounceFn(_setLoading, 150);
const _setRequestFinished = () => {
	requestFinished.value = true;
	isLoading.value = false;
};

const userInput = ref("");
const userInputMaxlength = 150;
// android fix for not updated v-model value on input TUB-16479
const setUserInput = (newInput: string) => {
	userInput.value = newInput;
};
let searchQuery = "";
const isSearchMostSuggested = computed(() => userInput.value.length < 3);
const isSearchVisible = ref(false);

const { setFindings } = useFindings();
const _epoqRequest = async (requestUrl: string) => {
	const requestParams = getEpoqRequestParams(searchQuery);
	const jsonpUrl = new URL(`${requestUrl}?${new URLSearchParams(requestParams)}`).toString();
	requestFinished.value = false;
	_debounceLoading();

	try {
		const response = await fetchJsonp(jsonpUrl);
		if (!response.ok) throw new Error("Epoq Search request failed");

		const { result }: { result: EpoqResult } = await response.json();
		setFindings(result);
	} catch (err: unknown) {
		console.error(err);
	} finally {
		_setRequestFinished();
	}
};

let useFallback = false;
const setUpRequest = async () => {
	try {
		await Promise.race([_epoqRequest(useFallback ? EPOQ_SEARCH_FALLBACK_URL : EPOQ_SEARCH_URL), epoqRequestTimeout()]);
	} catch (err: unknown) {
		console.error(err);
		if (useFallback) return;

		useFallback = true;
		// retry with fallback URL
		setUpRequest();
		// reset to default URL after 5 minutes
		setTimeout(() => {
			useFallback = false;
		}, EPOQ_SEARCH_FALLBACK_TIMEOUT);
	}
};

const _debounceRequest = useDebounceFn(setUpRequest, 200);
const inputEl = ref<HTMLInputElement | null>(null);

const triggerTypeAhead = () => {
	if (isSearchMostSuggested.value) return;

	searchQuery = userInput.value;
	_debounceRequest();
};

const focusSearch = () => {
	inputEl?.value?.focus();
};

const setInputElementRef = (ref: HTMLInputElement | null) => {
	inputEl.value = ref;
};

const resetSearch = () => {
	userInput.value = "";
	inputEl?.value?.blur();
	OcmMessenger.publish(messengerPublicationTypes.CLOSE_SEARCH);
};

const openSearch = () => {
	if (isSearchVisible.value) {
		focusSearch();
		return;
	}

	useMostSuggestedRequest().loadSuggestions();
	isSearchVisible.value = true;
	nextTick(() => {
		focusSearch();
	});
	triggerTypeAhead();
};

const closeSearch = () => {
	if (!isSearchVisible.value) return;
	isSearchVisible.value = false;
	inputEl?.value?.blur();
};

const deleteSearch = () => {
	userInput.value = "";
	focusSearch();
	triggerTypeAhead();
};

const { departmentCapitalized, department } = useDepartment();
const _navigateToShopSearchResult = (searchEncoded: string) => {
	const url = `${searchPagePath}?s=${searchEncoded}&department=${department.value}#/${searchCategoryHashLabel}/${departmentCapitalized.value}`;

	if (window.ocmOrchestrator) {
		window.ocmOrchestrator.spaNavigateUrl(url);
		OcmMessenger.publish(messengerPublicationTypes.SEARCH_SUBMITTED);
		resetSearch();
	} else {
		window.location.assign(url);
	}
};

enum triggerType {
	click = "textsearch",
	keyboard = "textsearch",
	voice = "voicesearch",
}

const triggerSearch = (trigger: "voice" | "click" | "keyboard" = "keyboard") => {
	const navigated = navigateToSelectedTypeAheadItem();
	if (navigated || userInput.value.length < 1) return;

	tr_searchEvent({ searchterm: userInput.value, type: triggerType[trigger] });

	const searchEncoded = encodeURIComponent(userInput.value.substring(0, userInputMaxlength).trim());
	_navigateToShopSearchResult(searchEncoded);

	if (window.EpoqNS) {
		console.log("%c%s", "color:#fff;background-color:#35495e;", "search: EpoqNS.epoqForeignInit");
		const stateUrl = window.location.href;
		window.EpoqNS.epoqForeignInit({ stateUrl });
	}
};

function navigateToSelectedTypeAheadItem() {
	if (focusedTypeAheadIndex.value > -1 && focusedTypeAheadId.value) {
		const linkDestination = document.getElementById(focusedTypeAheadId.value)?.getAttribute("href");
		if (linkDestination) {
			window.location.assign(linkDestination);
			return true;
		}
	}
	return false;
}

export const onInput = (target: HTMLInputElement) => {
	setUserInput(target.value);
	focusedTypeAheadIndex.value = -1;
	focusedTypeAheadId.value = undefined;
	triggerTypeAhead();
};

export const onKeyDown = (event: KeyboardEvent) => {
	switch (event.key) {
		case "ArrowUp":
			handleArrowUpAndDown(-1);
			event.preventDefault(); // prevent scrolling when focus is on submit or microphone button and from moving cursor
			break;
		case "ArrowDown":
			handleArrowUpAndDown(1);
			event.preventDefault();
			break;
	}
};

const { mostSuggestedItems } = useMostSuggestedRequest();
const nrOfDisplayedTypeAheadItems = computed(() =>
	isSearchMostSuggested.value ? mostSuggestedItems.value.mostSearchItems.length : findingsSize.value
);

const handleArrowUpAndDown = (movementIndex: number) => {
	const totalItems = nrOfDisplayedTypeAheadItems.value;
	focusedTypeAheadIndex.value = (focusedTypeAheadIndex.value + movementIndex + totalItems) % totalItems;
};

export default function useSearch() {
	return {
		isLoading,
		requestFinished,
		setUpRequest,
		userInput,
		userInputMaxlength,
		setInputElementRef,
		isSearchVisible,
		isSearchMostSuggested,
		openSearch,
		closeSearch,
		focusSearch,
		deleteSearch,
		triggerSearch,
		resetSearch,
		onKeyDown,
		onInput,
		nrOfDisplayedTypeAheadItems,
		searchPagePath,
		searchCategoryHashLabel,
	};
}
